import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Download, Star } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { buildApiFilterString } from '@/hooks/useFilters';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import { getFilterOptions } from '@/services/filters.service';
import { leadSourceFilterConfig, useWithLeadSourceOptions } from '@/hooks/filters/leadSourceOptions';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Button, DataTable } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import type { BadgeVariant } from '@/types/common.types';
import { type FilterConfig, type FilterOption } from '@/types/filters.types';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from '@/store/toastStore';
import * as leadsSvc from '@/services/leads.service';
import type { Lead, LeadTab, LeadStatus } from '@/services/leads.service';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { LeadDetailModal } from '@/pages/leads/LeadDetailModal';
import { LeadImportButton } from '@/pages/leads/LeadImportButton';
import { formatMarketingSource } from '@/pages/leads/marketingSource';
import { stripHtmlToText } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useAuth } from '@/hooks/useAuth';
import { columnsForContainerWidth } from '@/components/ui/responsiveColumns';
import { LEAD_COLUMN_THRESHOLDS } from '@/pages/leads/leadColumns';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search leads...' },
  { key: 'daterange', label: 'Date', type: 'daterange' },
  { key: 'lead-status', label: 'Lead Status', type: 'select', allLabel: 'All statuses', multi: true },
  { key: 'lead-assign', label: 'Assign Status', type: 'select', allLabel: 'All', multi: true },
  leadSourceFilterConfig('Lead Source', 'All sources'),
  { key: 'origin', label: 'Origin', type: 'select', allLabel: 'All origins', multi: true, searchable: true },
  { key: 'utm-campaign', label: 'Campaign', type: 'select', allLabel: 'All campaigns', multi: true },
  { key: 'utm-source', label: 'UTM Source', type: 'select', allLabel: 'All UTM sources', multi: true },
  { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All types', multi: true },
  { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
  { key: 'users', label: 'User', type: 'select', allLabel: 'All users', multi: true },
  { key: 'groups', label: 'Group', type: 'select', allLabel: 'All groups', multi: true },
];

/** Which `/leads` tab to request based on the Lead Status filter (converted column). */
// Always use 'all' tab (V2 engine) for performance; status filtering is handled
// server-side via filtersArray.

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeTimer(dateAdded: string): string {
  const diff = Date.now() - new Date(dateAdded).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function truncate(text: string | null, max = 60): string {
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function leadMessageText(row: Lead): string {
  return stripHtmlToText(row.message ?? row.notes);
}

function formatDateMDY(dateStr: string): string {
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function formatDateTimeMDY(dateStr: string): string {
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${mm}-${dd}-${yyyy} ${h12}:${min} ${ampm}`;
}

function formatNameLastFirst(row: Lead): string {
  if (row.lastName && row.firstName) return `${row.lastName}, ${row.firstName}`;
  return row.fullName;
}

function statusBadgeVariant(status: LeadStatus): BadgeVariant {
  switch (status) {
    case 'new': return 'info';
    case 'contacted': return 'warning';
    case 'qualified': return 'success';
    case 'converted': return 'success';
    case 'dead': return 'error';
    case 'spam': return 'error';
    case 'trash': return 'error';
  }
}

function statusLabel(status: LeadStatus): string {
  switch (status) {
    case 'spam': return 'Spam';
    case 'trash': return 'Trash';
    case 'dead': return 'Dead';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function renderStarRating(level: number | null): JSX.Element {
  if (level == null) return <span className="text-slate-400 text-sm">—</span>;
  const stars = Math.min(Math.max(level, 0), 5);
  return (
    <span className="inline-flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < stars ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
        />
      ))}
    </span>
  );
}

function isEmailLead(row: Lead): boolean {
  return (row.leadKind ?? 'email') === 'email';
}

/**
 * The Lead Source cell. A request-info lead also names the message that
 * delivered the link ("Spring Botox · SMS · Botox Package") on a second line —
 * the same wording as the lead detail, from the same formatter.
 */
function renderLeadSourceCell(row: Lead, textClass: string): JSX.Element {
  const source = row.marketingSource;
  if (!source) return <span className={`text-slate-700 ${textClass}`}>{row.leadSourceName ?? '—'}</span>;
  const line = formatMarketingSource(source);
  return (
    <div className="min-w-0">
      <span className={`block text-slate-700 ${textClass}`}>{row.leadSourceName ?? 'Request-info link'}</span>
      <span
        className="block max-w-[240px] truncate text-[11px] text-slate-500"
        title={formatMarketingSource(source, { includeReferral: true })}
      >
        {line}
        {source.referral && <span className="ml-1 font-semibold text-purple-700">Referral</span>}
      </span>
    </div>
  );
}

// ─── Column definitions per tab ───────────────────────────────────────────────

/**
 * Encode the current assignment to legacy `<option value>` form so the
 * dropdown's selected option matches a real `<option>`.
 *   - active role -> "role_<roleID>"
 *   - active user -> "<userID>"
 *   - neither     -> "" (None)
 */
function currentAssignValue(row: Lead): string {
  if (row.assignedRoleId && row.assignedRoleId > 0) return `role_${row.assignedRoleId}`;
  if (row.assignedToId && row.assignedToId > 0) return String(row.assignedToId);
  return '';
}

interface AssigneeContext {
  /** Active roles/groups for the current account. */
  groups: FilterOption[];
  /** Active users for the current account. */
  users: FilterOption[];
  /** Fire the assignment write to the API. */
  onAssign: (leadId: number, value: string) => void;
  /** Lead currently being saved (for spinner / disabled state). */
  pendingLeadId: number | null;
}

function renderAssignSelect(row: Lead, ctx: AssigneeContext): JSX.Element {
  const value = currentAssignValue(row);
  const isPending = ctx.pendingLeadId === row.leadId;
  return (
    <select
      className="block w-full max-w-[170px] rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-slate-100"
      value={value}
      disabled={isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        const next = e.target.value;
        if (next === value) return;
        ctx.onAssign(row.leadId, next);
      }}
    >
      <option value="">None</option>
      {ctx.groups.length > 0 && (
        <optgroup label="Groups">
          {ctx.groups.map((g) => (
            <option key={`r-${g.value}`} value={`role_${g.value}`}>
              {g.label}
            </option>
          ))}
        </optgroup>
      )}
      {ctx.users.length > 0 && (
        <optgroup label="Users">
          {ctx.users.map((u) => (
            <option key={`u-${u.value}`} value={u.value}>
              {u.label}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function getColumns(
  tab: LeadTab,
  navigate: ReturnType<typeof useNavigate>,
  openLeadDetail: (leadId: number) => void,
  assignCtx: AssigneeContext,
  /** The account's own wording for the Coordinator role. */
  coordLabel: string,
): DataTableColumn<Lead>[] {
  const idCol: DataTableColumn<Lead> = {
    key: 'leadId',
    header: 'ID',
    render: (row) =>
      isEmailLead(row) ? (
        <button
          type="button"
          className="text-primary hover:underline font-medium text-left"
          onClick={(e) => { e.stopPropagation(); openLeadDetail(row.leadId); }}
        >
          {row.leadId}
        </button>
      ) : (
        <span className="font-mono text-xs text-slate-500">{row.leadId || '—'}</span>
      ),
  };

  const receivedCol: DataTableColumn<Lead> = {
    key: 'dateAdded',
    header: 'Received',
    sortable: true,
    role: 'subtitle',
    width: 'whitespace-nowrap',
    render: (row) => (
      <div className="leading-tight">
        <div className="text-slate-600 text-xs">{formatDateMDY(row.dateAdded)}</div>
        <div className="text-slate-400 text-[11px] font-mono">{computeTimer(row.dateAdded)}</div>
      </div>
    ),
  };

  const nameCol: DataTableColumn<Lead> = {
    key: 'fullName',
    header: 'Name',
    sortable: true,
    // Card-only. With no role declared anywhere in the set DataTable promotes the FIRST
    // column, which is the status Badge — a card headed by a pill and then listing six
    // labelled fields. `role` is ignored in table mode, so desktop is untouched.
    role: 'title',
    render: (row) => {
      const name = row.fullName ? formatNameLastFirst(row) : 'Unknown';
      if (!isEmailLead(row) && row.patientId) {
        return (
          <button
            type="button"
            className="text-primary hover:underline font-medium text-left text-xs"
            onClick={(e) => { e.stopPropagation(); navigate(`/patients/${row.patientId}`); }}
          >
            {name}
          </button>
        );
      }
      if (!isEmailLead(row)) {
        return <span className="text-slate-400 text-xs">{name}</span>;
      }
      return (
        <button
          type="button"
          className="text-primary hover:underline font-medium text-left text-xs"
          onClick={(e) => { e.stopPropagation(); openLeadDetail(row.leadId); }}
        >
          {name}
        </button>
      );
    },
  };

  const planIdCol: DataTableColumn<Lead> = {
    key: 'planId',
    header: 'Plan ID',
    render: (row) =>
      row.planId ? (
        <button
          type="button"
          className="text-primary hover:underline font-medium text-left"
          onClick={(e) => { e.stopPropagation(); navigate(`/plans/${row.planId}`); }}
        >
          {row.planId}
        </button>
      ) : (
        <span className="text-slate-400 text-sm">—</span>
      ),
  };

  const messageCol: DataTableColumn<Lead> = {
    key: 'message',
    header: 'Message',
    render: (row) => {
      const messageText = leadMessageText(row);
      return (
        <span className="text-slate-500 text-xs" title={messageText || '—'}>
          {truncate(messageText)}
        </span>
      );
    },
  };

  const assignedToCol: DataTableColumn<Lead> = {
    key: 'assignedToName',
    header: 'Assigned To',
    render: (row) =>
      isEmailLead(row)
        ? renderAssignSelect(row, assignCtx)
        : <span className="text-slate-700 text-sm">{row.assignedToName ?? '—'}</span>,
  };

  switch (tab) {
    case 'all':
      return [
        {
          key: 'status',
          header: 'Status',
          width: 'whitespace-nowrap',
          render: (row) => (
            <Badge
              variant={statusBadgeVariant(row.status)}
              className={`text-[10px] px-1.5 py-0.5${row.status === 'spam' || row.status === 'trash' ? ' bg-red-100 text-red-800 ring-red-300' : ''}`}
            >
              {statusLabel(row.status)}
            </Badge>
          ),
        },
        receivedCol,
        nameCol,
        {
          key: 'subject',
          header: 'Subject',
          render: (row) => (
            <span className="text-slate-700 text-xs max-w-[220px] inline-block break-words">
              {row.subject ?? '—'}
            </span>
          ),
        },
        {
          key: 'leadFrom',
          header: 'Origin',
          width: 'max-w-[160px]',
          render: (row) => <span className="text-slate-700 text-xs block truncate" title={row.leadFrom ?? row.formOrigin ?? ''}>{row.leadFrom ?? row.formOrigin ?? '—'}</span>,
        },
        {
          key: 'leadSourceName',
          header: 'Lead Source',
          render: (row) => renderLeadSourceCell(row, 'text-xs'),
        },
        {
          key: 'utmSource',
          header: 'UTM Source',
          width: 'max-w-[160px]',
          render: (row) => (
            <span className="text-slate-700 text-xs block truncate" title={row.utmSource ?? ''}>
              {row.utmSource || '—'}
            </span>
          ),
        },
        {
          key: 'facilityName',
          header: 'Facility',
          width: 'max-w-[160px]',
          render: (row) => (
            <span className="text-slate-700 text-xs block truncate" title={row.facilityName ?? ''}>
              {row.facilityName ?? '—'}
            </span>
          ),
        },
        {
          key: 'planTypeName',
          header: 'Plan Type',
          render: (row) =>
            row.planId ? (
              <button
                type="button"
                className="text-primary hover:underline font-medium text-left text-xs"
                onClick={(e) => { e.stopPropagation(); navigate(`/plans/${row.planId}`); }}
              >
                {row.planTypeName ?? '—'}
              </button>
            ) : (
              <span className="text-slate-700 text-xs">{row.planTypeName ?? '—'}</span>
            ),
        },
        assignedToCol,
        messageCol,
      ];

    case 'new':
      return [
        idCol,
        receivedCol,
        nameCol,
        {
          key: 'subject',
          header: 'Subject',
          render: (row) => <span className="text-slate-700 text-sm">{truncate(row.subject, 40)}</span>,
        },
        {
          key: 'leadFrom',
          header: 'Origin',
          render: (row) => <span className="text-slate-700 text-sm">{row.leadFrom ?? row.formOrigin ?? '—'}</span>,
        },
        {
          key: 'leadSourceName',
          header: 'Lead Source',
          render: (row) => renderLeadSourceCell(row, 'text-sm'),
        },
        {
          key: 'utmSource',
          header: 'UTM Source',
          render: (row) => <span className="text-slate-600 text-sm">{row.utmSource ?? '—'}</span>,
        },
        {
          key: 'utmMedium',
          header: 'UTM Medium',
          render: (row) => <span className="text-slate-600 text-sm">{row.utmMedium ?? '—'}</span>,
        },
        {
          key: 'utmCampaign',
          header: 'UTM Campaign',
          render: (row) => <span className="text-slate-600 text-sm">{row.utmCampaign ?? '—'}</span>,
        },
        {
          key: 'interestLevel',
          header: 'Interest',
          render: (row) => renderStarRating(row.interestLevel),
        },
        {
          key: 'planTypeName',
          header: 'Plan Type',
          render: (row) => <span className="text-slate-700 text-sm">{row.planTypeName ?? '—'}</span>,
        },
        {
          key: 'status',
          header: 'Status',
          render: (row) => (
            <Badge variant={statusBadgeVariant(row.status)} className="text-xs">
              {statusLabel(row.status)}
            </Badge>
          ),
        },
        assignedToCol,
        messageCol,
      ];

    case 'bad':
      return [
        idCol,
        receivedCol,
        nameCol,
        {
          key: 'assignedToName',
          header: 'Last Updated By',
          render: (row) => <span className="text-slate-700 text-sm">{row.assignedToName ?? '—'}</span>,
        },
        {
          key: 'badReason',
          header: 'Reason',
          render: (row) => <span className="text-slate-700 text-sm">{row.badReason ?? '—'}</span>,
        },
        messageCol,
      ];

    case 'done':
      return [
        idCol,
        receivedCol,
        nameCol,
        {
          key: 'coordinatorName',
          header: coordLabel,
          render: (row) => <span className="text-slate-700 text-sm">{row.coordinatorName ?? '—'}</span>,
        },
        {
          key: 'lastContact',
          header: 'Last Comm',
          sortable: true,
          render: (row) => (
            <span className="text-slate-500 text-sm" title={leadMessageText(row) || '—'}>
              {row.lastContact ? formatDateMDY(row.lastContact) : '—'}
            </span>
          ),
        },
        messageCol,
        planIdCol,
      ];

    case 'sms':
      return [
        {
          key: 'dateAdded',
          header: 'Received',
          sortable: true,
          render: (row) => (
            <span className="text-slate-600 text-sm">{formatDateTimeMDY(row.dateAdded)}</span>
          ),
        },
        {
          key: 'callerIdNumber',
          header: 'Caller ID',
          render: (row) => <span className="text-slate-700 text-sm">{row.callerIdNumber ?? '—'}</span>,
        },
        {
          key: 'fullName',
          header: 'Patient',
          sortable: true,
          render: (row) =>
            row.fullName && row.patientId ? (
              <button
                type="button"
                className="text-primary hover:underline font-medium text-left"
                onClick={(e) => { e.stopPropagation(); navigate(`/patients/${row.patientId}`); }}
              >
                {formatNameLastFirst(row)}
              </button>
            ) : (
              <span className="text-slate-400 text-sm">Unknown</span>
            ),
        },
        planIdCol,
        {
          key: 'message',
          header: 'Message',
          render: (row) => <span className="text-slate-500 text-sm" title={leadMessageText(row) || '—'}>{truncate(leadMessageText(row), 80)}</span>,
        },
      ];

    case 'forms':
      return [
        {
          key: 'subject',
          header: 'Form',
          render: (row) => <span className="text-slate-700 text-sm font-medium">{row.subject ?? '—'}</span>,
        },
        {
          key: 'fullName',
          header: 'Patient',
          sortable: true,
          render: (row) =>
            row.patientId ? (
              <button
                type="button"
                className="text-primary hover:underline font-medium text-left"
                onClick={(e) => { e.stopPropagation(); navigate(`/patients/${row.patientId}`); }}
              >
                {row.fullName ? formatNameLastFirst(row) : 'Unknown'}
              </button>
            ) : (
              <span className="text-slate-400 text-sm">{row.fullName ? formatNameLastFirst(row) : 'Unknown'}</span>
            ),
        },
        planIdCol,
        {
          key: 'dateAdded',
          header: 'Date',
          sortable: true,
          render: (row) => (
            <span className="text-slate-600 text-sm">{formatDateMDY(row.dateAdded)}</span>
          ),
        },
      ];
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Where Leads switches to cards. Passed explicitly to `DataTable` as well as read here, so
 * the two can never disagree about which tier is which — `columnsForContainerWidth` keys its
 * whole behaviour off this answer.
 */
const LEADS_CARD_BREAKPOINT = 'md' as const;

export default function LeadsPage() {
  const navigate = useNavigate();
  // Feeds the Import button, which also needs the Lead Import module (see
  // LeadImportButton).
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const updateAIContext = usePageContext('Leads');

  // ─── State ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);

  const filterState = useLocalDashboardFilters(FILTER_CONFIGS, 'leads');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const { data: bulkFilterOptions } = useQuery({
    queryKey: ['lead-filter-options'],
    queryFn: () => getFilterOptions(
      FILTER_CONFIGS.filter((c) => c.type === 'select').map((c) => c.key),
    ),
    staleTime: 5 * 60 * 1000,
  });

  // Lead source comes from the shared hierarchy builder rather than the bulk
  // endpoint, so this list's tree matches the Plans list exactly.
  const filterOptions = useWithLeadSourceOptions(bulkFilterOptions);

  const searchValue = useMemo(() => {
    const raw = effectiveValues.search;
    return Array.isArray(raw) ? raw[0] ?? '' : '';
  }, [effectiveValues.search]);
  const search = useDebounce(searchValue, 300);
  const dateRange = useMemo(() => {
    const raw = effectiveValues.daterange;
    return raw && !Array.isArray(raw) && 'from' in raw ? raw : { from: '', to: '' };
  }, [effectiveValues.daterange]);
  // filterPlus carries the sidebar's +/- toggles (`key:1` == negative); the
  // API ignores negations unless it travels beside filtersArray.
  const { filtersArray, filterPlus } = useMemo(
    () => buildApiFilterString(effectiveValues, filterState.negations),
    [effectiveValues, filterState.negations],
  );

  const listTab: LeadTab = 'all';

  useEffect(() => {
    setPage(1);
  }, [filtersArray, filterPlus, search]);

  // ─── Queries ──────────────────────────────────────────────────────────

  const { data: leadsData, isLoading, isError } = useQuery({
    queryKey: ['leads', { tab: listTab, search, filtersArray, filterPlus, page }],
    queryFn: () =>
      leadsSvc.listLeads({
        tab: listTab,
        search: search || undefined,
        filtersArray: filtersArray || undefined,
        filterPlus: filterPlus || undefined,
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
        page,
        perPage: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  // Show spinner on filter click, hide when query delivers fresh data.
  const [filterSpinner, setFilterSpinner] = useState(false);

  useEffect(() => {
    setFilterSpinner(false);
  }, [leadsData]);

  const showListSpinner = filterSpinner;

  const handleFilterChange = useCallback(
    (...args: Parameters<typeof filterState.setFilter>) => {
      setFilterSpinner(true);
      filterState.setFilter(...args);
    },
    [filterState],
  );

  const handleFilterClear = useCallback(
    (...args: Parameters<typeof filterState.clearFilter>) => {
      setFilterSpinner(true);
      filterState.clearFilter(...args);
    },
    [filterState],
  );

  const assignMutation = useMutation({
    mutationFn: ({ leadId, value }: { leadId: number; value: string }) =>
      leadsSvc.assignLead(leadId, value),
    onSuccess: () => {
      toast.success('Lead assignment updated');
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: () => toast.error('Failed to update lead assignment'),
  });

  // ─── Derived data ────────────────────────────────────────────────────

  const leads = leadsData?.data ?? [];
  const total = leadsData?.meta?.total ?? 0;

  // ─── AI Page Context ──────────────────────────────────────────────────
  useEffect(() => {
    const parts: string[] = [`list=${listTab}`];
    if (search) parts.push(`search="${search}"`);
    if (filtersArray) parts.push(`filters=${filtersArray}`);
    if (filterPlus.includes(':1')) parts.push(`negated=${filterPlus}`);

    updateAIContext({
      filters: parts.join(', '),
      dataSummary: `${total} results, page ${page}, list: ${listTab}`,
    });
  }, [listTab, search, filtersArray, filterPlus, total, page, updateAIContext]);

  const openLeadDetail = useCallback((leadId: number) => {
    setSelectedLeadId(leadId);
  }, []);

  const closeLeadDetail = useCallback(() => {
    setSelectedLeadId(null);
  }, []);

  const handleRowClick = useCallback((row: Lead) => {
    if (isEmailLead(row)) {
      openLeadDetail(row.leadId);
      return;
    }
    if (row.planId) {
      navigate(`/plans/${row.planId}`);
      return;
    }
    if (row.patientId) {
      navigate(`/patients/${row.patientId}`);
    }
  }, [navigate, openLeadDetail]);

  const assignCtx: AssigneeContext = useMemo(() => ({
    groups: filterOptions?.groups ?? [],
    users: filterOptions?.users ?? [],
    onAssign: (leadId, value) => assignMutation.mutate({ leadId, value }),
    pendingLeadId: assignMutation.isPending
      ? (assignMutation.variables?.leadId ?? null)
      : null,
  }), [filterOptions, assignMutation]);

  const { roles: accountRoleTitles } = useRoleTitles();
  const coordLabel = accountRoleTitles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const allColumns = getColumns('all', navigate, openLeadDetail, assignCtx, coordLabel);

  // Responsive column hiding — remove columns from right as container shrinks.
  // Order of removal: LEAD_COLUMN_THRESHOLDS (pages/leads/leadColumns.ts).
  //
  // Below `md` this does not apply at all: DataTable renders cards there and builds their
  // labelled fields from this same array, so filtering here removed Subject, Origin and
  // Facility from the card with no header row or horizontal scroll to reach them by
  // (SCRUM-1447). `columnsForContainerWidth` owns that branch.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(Infinity);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTableWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Unconditional, at the top level of the component. `useIsBelow` is a
  // useSyncExternalStore hook and must not sit behind a `&&` — that shape short-circuited
  // the call, changed the hook count between renders and blanked every page with React #310.
  const asCards = useIsBelow(LEADS_CARD_BREAKPOINT);

  const columns = useMemo(
    () => columnsForContainerWidth(allColumns, tableWidth, LEAD_COLUMN_THRESHOLDS, asCards),
    [allColumns, tableWidth, asCards],
  );

  // ─── Export handler ───────────────────────────────────────────────────

  async function handleExport() {
    try {
      const blob = await leadsSvc.exportLeadsCsv({
        tab: listTab,
        search,
        filtersArray: filtersArray || undefined,
        filterPlus: filterPlus || undefined,
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${listTab}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export leads');
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={effectiveValues}
            options={filterOptions ?? {}}
            negations={filterState.negations}
            onChange={handleFilterChange}
            onNegateChange={filterState.setNegation}
            onClear={handleFilterClear}
            searchCommitMode="blur-or-enter"
            storageKey="leads"
            showAIAssistant={false}
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title="Leads"
              actions={
                <div className="flex items-center gap-2">
                  <LeadImportButton isAdmin={isAdmin} />
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => navigate('/leads/new')}
                  >
                    Add Patient
                  </Button>
                </div>
              }
            />

          </div>

          <div ref={tableContainerRef} className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            {showListSpinner && leads.length > 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-primary" />
              </div>
            )}
            <DataTable<Lead>
              data={leads}
              columns={columns}
              keyField="rowKey"
              cardBreakpoint={LEADS_CARD_BREAKPOINT}
              className="[&>div]:overflow-x-hidden"
              loading={isLoading}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              onRowClick={handleRowClick}
              emptyTitle={isError ? 'Unable to load leads' : 'No leads found'}
              emptyDescription={
                isError
                  ? 'Something went wrong while loading leads. Please refresh to try again. If this keeps happening, the leads service may be returning an error.'
                  : 'Try adjusting your search or filters, or add a new lead to get started.'
              }
            />
          </div>
        </div>
      </ListPageLayout>
      <LeadDetailModal
        isOpen={selectedLeadId !== null}
        onClose={closeLeadDetail}
        leadId={selectedLeadId}
      />
    </>
  );
}
